import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { StoreRequestStateUpdater } from 'src/app/shared/types/store-request-state-updater';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { AccountInfo } from '../types/account.info';
import { AUTH_CONFIG } from '../auth.config';
import { ApiResponse } from 'src/app/shared/types/api-response';
import { catchError, map, timeout } from 'rxjs/operators';
import { UserToken } from '../types/user.token';
import * as moment from 'moment';
import { SHORT_TIMEOUT, TIMEOUT } from 'src/app/shared/types/request-state';
import { AccountNotification } from '../types/account.notification';
import { Log } from '../../../features/administration/logs/types/log';

@Injectable()
export class AuthEndpoint {
  constructor(private http: HttpClient) {}

  getAccountInfo(forceLogout: boolean, requestStateUpdater: StoreRequestStateUpdater): Observable<AccountInfo> {
    const request = AUTH_CONFIG.requests.getAccountInfo;
    requestStateUpdater(request.name, { inProgress: true, success: false });
    let options = {};
    if (forceLogout) {
      options = {
        params: new HttpParams().append('forceLogout', forceLogout.toString()),
      };
    }
    return this.http.get<ApiResponse<AccountInfo>>(request.url, options).pipe(
      timeout(TIMEOUT),
      map((response) => {
        requestStateUpdater(request.name, { inProgress: false, success: true });
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        requestStateUpdater(request.name, {
          inProgress: false,
          error: true,
          status: error.status,
        });
        return throwError(error);
      })
    );
  }

  requestForToken(
    email: string,
    password: string,
    requestStateUpdater: StoreRequestStateUpdater
  ): Observable<UserToken> {
    const request = AUTH_CONFIG.requests.requestForToken;
    requestStateUpdater(request.name, { inProgress: true, success: false });

    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);
    params.append('grant_type', 'password');
    params.append('client_id', 'back_application');

    return this.http
      .post<any>(request.url, params.toString(), {
        headers: AUTH_CONFIG.headers.authApiHeader,
      })
      .pipe(
        timeout(SHORT_TIMEOUT),
        map((response) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            success: true,
          });
          return {
            expirationDate: moment().add(response.expires_in, 'second'),
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
          };
        }),
        catchError((error: HttpErrorResponse) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            error: true,
            status: error instanceof TimeoutError ? -1 : error.status,
          });
          return throwError(error);
        })
      );
  }

  refreshToken(refreshToken: string, requestStateUpdater: StoreRequestStateUpdater): Observable<UserToken> {
    const request = AUTH_CONFIG.requests.refreshToken;
    requestStateUpdater(request.name, { inProgress: true });

    const params = new URLSearchParams();
    params.append('refresh_token', refreshToken);
    params.append('grant_type', 'refresh_token');
    params.append('client_id', 'back_application');

    return this.http
      .post<any>(request.url, params.toString(), {
        headers: AUTH_CONFIG.headers.authApiHeader,
      })
      .pipe(
        timeout(TIMEOUT),
        map((response) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            success: true,
            error: false,
          });
          return {
            expirationDate: moment().add(response.expires_in, 'second'),
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
          };
        }),
        catchError((error: HttpErrorResponse) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            error: true,
            success: false,
          });
          return throwError(error);
        })
      );
  }

  requestForgottenPassword(email: string, requestStateUpdater: StoreRequestStateUpdater): Observable<boolean> {
    const request = AUTH_CONFIG.requests.requestForgottenPassword;
    requestStateUpdater(request.name, { inProgress: true });
    return this.http
      .post<ApiResponse<boolean>>(request.url, { email })
      .pipe(
        timeout(TIMEOUT),
        map((response) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            success: response.success,
          });
          return response.data;
        }),
        catchError((error: HttpErrorResponse) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            error: true,
            success: false,
          });
          return throwError(error);
        })
      );
  }

  clearUnreadNotifications(requestStateUpdater: StoreRequestStateUpdater): Observable<boolean> {
    const request = AUTH_CONFIG.requests.clearUnreadNotifications;
    requestStateUpdater(request.name, { inProgress: true, success: false });

    return this.http.patch<ApiResponse<boolean>>(request.url, {}).pipe(
      timeout(TIMEOUT),
      map((response) => {
        requestStateUpdater(request.name, { inProgress: false, success: true });
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        requestStateUpdater(request.name, { inProgress: false, error: true, status: error.status });
        return throwError(error);
      })
    );
  }

  markAsRead(notificationUuid: string, requestStateUpdater: StoreRequestStateUpdater): Observable<boolean> {
    const request = AUTH_CONFIG.requests.markAsRead;
    requestStateUpdater(request.name, { inProgress: true, success: false });

    return this.http.patch<ApiResponse<boolean>>(request.url, notificationUuid).pipe(
      timeout(TIMEOUT),
      map((response) => {
        requestStateUpdater(request.name, { inProgress: false, success: true });
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        requestStateUpdater(request.name, { inProgress: false, error: true, status: error.status });
        return throwError(error);
      })
    );
  }
  getAccountNotificationsStats(
    size: number,
    page: number,
    requestStateUpdater: StoreRequestStateUpdater
  ): Observable<AccountNotification> {
    const request = AUTH_CONFIG.requests.getAccountNotificationsStats;
    requestStateUpdater(request.name, { inProgress: true, success: false });
    const options = {
      params: new HttpParams().append('size', size.toString()).append('page', page.toString()),
    };
    return this.http.get<ApiResponse<AccountNotification>>(request.url, options).pipe(
      timeout(TIMEOUT),
      map((response) => {
        requestStateUpdater(request.name, { inProgress: false, success: true });
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        requestStateUpdater(request.name, { inProgress: false, error: true, status: error.status });
        return throwError(error);
      })
    );
  }

  getAccountNotifications(
    size: number,
    page: number,
    requestStateUpdater: StoreRequestStateUpdater
  ): Observable<AccountNotification> {
    const request = AUTH_CONFIG.requests.getAccountNotifications;
    requestStateUpdater(request.name, { inProgress: true, success: false });
    const options = {
      params: new HttpParams().append('size', size.toString()).append('page', page.toString()),
    };
    return this.http.get<ApiResponse<AccountNotification>>(request.url, options).pipe(
      timeout(TIMEOUT),
      map((response) => {
        requestStateUpdater(request.name, { inProgress: false, success: true });
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        requestStateUpdater(request.name, { inProgress: false, error: true, status: error.status });
        return throwError(error);
      })
    );
  }

  markAllAsRead(requestStateUpdater: StoreRequestStateUpdater): Observable<boolean> {
    const request = AUTH_CONFIG.requests.markAllAsRead;
    requestStateUpdater(request.name, { inProgress: true, success: false });
    return this.http.patch<ApiResponse<boolean>>(request.url, {}).pipe(
      timeout(TIMEOUT),
      map((response) => {
        requestStateUpdater(request.name, { inProgress: false, success: true });
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        requestStateUpdater(request.name, { inProgress: false, error: true, status: error.status });
        return throwError(error);
      })
    );
  }

  logAction(actionType: string, requestStateUpdater: StoreRequestStateUpdater): Observable<Log> {
    const request = AUTH_CONFIG.requests.logAction;
    requestStateUpdater(request.name, { inProgress: true });
    return this.http
      .post<ApiResponse<Log>>(request.url, { actionType })
      .pipe(
        timeout(TIMEOUT),
        map((response) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            success: response.success,
          });
          return response.data;
        }),
        catchError((error: HttpErrorResponse) => {
          requestStateUpdater(request.name, {
            inProgress: false,
            error: true,
            success: false,
          });
          return throwError(error);
        })
      );
  }
}
